import React, { useState } from 'react';

function InvestmentInput({ 
  initialInvestment, 
  onInvestmentChange, 
  targetNetValue, 
  onTargetNetValueChange,
  endDate,
  onEndDateChange,
  startDate,
  onStartDateChange,
  baseSalary,
  onBaseSalaryChange
}) {
  const [inputValue, setInputValue] = useState(initialInvestment);
  const [targetValue, setTargetValue] = useState(targetNetValue);
  const [dateValue, setDateValue] = useState(endDate);
  const [startDateValue, setStartDateValue] = useState(startDate);
  const [baseSalaryValue, setBaseSalaryValue] = useState(baseSalary);
  const [isEditing, setIsEditing] = useState(false);

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
    setInputValue(value);
  };

  const handleTargetChange = (e) => {
    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
    setTargetValue(value);
  };

  const handleBaseSalaryChange = (e) => {
    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
    setBaseSalaryValue(value);
  };

  const handleDateChange = (e) => {
    setDateValue(new Date(e.target.value));
  };

  const handleStartDateChange = (e) => {
    setStartDateValue(new Date(e.target.value));
  };

  const handleUpdate = () => {
    let hasChanges = false;
    
    if (inputValue > 0 && inputValue !== initialInvestment) {
      onInvestmentChange(inputValue);
      hasChanges = true;
    }
    
    if (targetValue > 0 && targetValue !== targetNetValue) {
      onTargetNetValueChange(targetValue);
      hasChanges = true;
    }

    if (baseSalaryValue !== baseSalary) {
      onBaseSalaryChange(baseSalaryValue);
      hasChanges = true;
    }
    
    if (startDateValue.getTime() !== startDate.getTime()) {
      onStartDateChange(startDateValue);
      hasChanges = true;
    }
    
    if (dateValue > startDateValue) {
      onEndDateChange(dateValue);
      hasChanges = true;
    } else if (dateValue <= startDateValue) {
      alert('Please select an end date after the start date');
      setDateValue(endDate);
      return;
    }
    
    if (hasChanges) {
      setIsEditing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUpdate();
    } else if (e.key === 'Escape') {
      setInputValue(initialInvestment);
      setTargetValue(targetNetValue);
      setDateValue(endDate);
      setStartDateValue(startDate);
      setBaseSalaryValue(baseSalary);
      setIsEditing(false);
    }
  };

  const formatDisplayValue = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="investment-input">
      <h3>Investment Parameters</h3>
      
      {isEditing ? (
        <div className="editing-row">
          <div className="input-group">
            <label>Initial Investment:</label>
            <input
              type="text"
              value={formatDisplayValue(inputValue)}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter amount"
            />
          </div>
          
          <div className="input-group">
            <label>Target Net Value:</label>
            <input
              type="text"
              value={formatDisplayValue(targetValue)}
              onChange={handleTargetChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter target amount"
            />
          </div>

          <div className="input-group">
            <label>Base Salary:</label>
            <input
              type="text"
              value={formatDisplayValue(baseSalaryValue)}
              onChange={handleBaseSalaryChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter annual salary"
            />
          </div>
          
          <div className="input-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={startDateValue.toISOString().split('T')[0]}
              onChange={handleStartDateChange}
              onKeyPress={handleKeyPress}
              max={dateValue.toISOString().split('T')[0]}
            />
          </div>
          
          <div className="input-group">
            <label>Target End Date:</label>
            <input
              type="date"
              value={dateValue.toISOString().split('T')[0]}
              onChange={handleDateChange}
              onKeyPress={handleKeyPress}
              min={startDateValue.toISOString().split('T')[0]}
            />
          </div>
          
          <div className="button-group">
            <button onClick={handleUpdate} className="update-btn">
              Update All
            </button>
            <button onClick={() => {
              setInputValue(initialInvestment);
              setTargetValue(targetNetValue);
              setDateValue(endDate);
              setStartDateValue(startDate);
              setBaseSalaryValue(baseSalary);
              setIsEditing(false);
            }} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="display-row">
          <div className="param-group">
            <label>Initial Investment:</label>
            <span className="value">{formatDisplayValue(initialInvestment)}</span>
          </div>
          
          <div className="param-group">
            <label>Target Net Value:</label>
            <span className="value">{formatDisplayValue(targetNetValue)}+</span>
          </div>

          <div className="param-group">
            <label>Base Salary:</label>
            <span className="value">{formatDisplayValue(baseSalary)}</span>
          </div>
          
          <div className="param-group">
            <label>Start Date:</label>
            <span className="value">{startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          
          <div className="param-group">
            <label>Target End Date:</label>
            <span className="value">{endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
          
          <button onClick={() => setIsEditing(true)} className="edit-btn">
            Edit Parameters
          </button>
        </div>
      )}
      
      <div className="tips">
        <p>💡 <strong>Tip:</strong> Edit any parameter above to see the entire investment plan update in real-time!</p>
      </div>
    </div>
  );
}

export default InvestmentInput;
